import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderPlus, Images } from 'lucide-react';
import { GALLERY_MEDIA_ENTITY_TYPE, type GalleryFolder } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { AddFolderDialog } from '../components/gallery/add-folder-dialog.js';
import { GalleryFolderTree } from '../components/gallery/gallery-folder-tree.js';
import { GalleryMediaGrid } from '../components/gallery/gallery-media-grid.js';
import { MoveMediaDialog } from '../components/gallery/move-media-dialog.js';
import { RenameFolderDialog } from '../components/gallery/rename-folder-dialog.js';
import { PageHeader } from '../components/layout/page-header.js';
import { MediaLightbox } from '../components/media/media-lightbox.js';
import { MediaUploader } from '../components/media/media-uploader.js';
import { Button, useConfirm, CardGridSkeleton } from '@tailoring-crm/ui';
import { galleryFolderLabel } from '../lib/gallery/gallery-folder-label.js';
import {
  useDeleteGalleryFolder,
  useFolderMedia,
  useGalleryFolders,
} from '../lib/gallery/use-gallery.js';

/**
 * The shop's design library, browsed as folders.
 *
 * Selecting a folder or the fixed Unfiled entry is the only thing that ever fires a media
 * request — nothing here fetches the whole gallery to filter it in the browser, and nothing
 * prefetches a sibling folder nobody has opened. That is also why nothing is selected on
 * load: opening this screen costs one folder-tree request and nothing else, until somebody
 * picks where to look.
 */

type Scope = string | 'unfiled';

const PAGE_SIZE = 24;
const UPLOAD_MAX_FILES = 24;

export function GalleryPage(): ReactElement {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const foldersQuery = useGalleryFolders();
  const folders = foldersQuery.data?.items ?? [];

  const [scope, setScope] = useState<Scope | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeMediaId, setActiveMediaId] = useState<string | null>(null);
  const [addingFolder, setAddingFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<GalleryFolder | null>(null);
  const [moving, setMoving] = useState(false);

  const deleteFolder = useDeleteGalleryFolder();
  const media = useFolderMedia(scope, { pageSize: PAGE_SIZE });
  const items = useMemo(() => media.data?.pages.flat() ?? [], [media.data]);

  const currentFolder =
    scope && scope !== 'unfiled' ? (folders.find((one) => one.id === scope) ?? null) : null;

  const resetSelection = (): void => {
    setSelecting(false);
    setSelectedIds(new Set());
  };

  const selectFolder = (folderId: string): void => {
    setScope(folderId);
    resetSelection();
  };

  const selectUnfiled = (): void => {
    setScope('unfiled');
    resetSelection();
  };

  const toggleSelect = (mediaId: string): void => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(mediaId)) next.delete(mediaId);
      else next.add(mediaId);
      return next;
    });
  };

  const askDeleteFolder = async (folder: GalleryFolder): Promise<void> => {
    const confirmed = await confirm({
      title: t('gallery.deleteFolder.title', { name: galleryFolderLabel(t, folder) }),
      description: t('gallery.deleteFolder.description'),
      confirmLabel: t('gallery.deleteFolder.confirm'),
      destructive: true,
    });
    if (!confirmed) return;
    await deleteFolder.mutateAsync(folder.id);
    if (scope === folder.id) setScope(null);
  };

  return (
    <>
      <PageHeader
        title={t('gallery.title')}
        description={t('gallery.description')}
        action={
          <Button onClick={() => setAddingFolder(true)}>
            <FolderPlus aria-hidden="true" />
            {t('gallery.addFolder.action')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
        <GalleryFolderTree
          folders={folders}
          selectedId={scope}
          onSelectFolder={selectFolder}
          onSelectUnfiled={selectUnfiled}
          onRename={setRenamingFolder}
          onDelete={(folder) => void askDeleteFolder(folder)}
        />

        <div className="flex flex-col gap-3">
          {scope === null ? (
            <EmptyState
              icon={Images}
              title={t('gallery.noFolderTitle')}
              description={t('gallery.noFolderDescription')}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">
                  {scope === 'unfiled'
                    ? t('gallery.unfiled')
                    : currentFolder
                      ? galleryFolderLabel(t, currentFolder)
                      : ''}
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelecting((current) => !current);
                      setSelectedIds(new Set());
                    }}
                  >
                    {selecting ? t('gallery.select.cancel') : t('gallery.select.start')}
                  </Button>
                  {selecting && selectedIds.size > 0 ? (
                    <Button size="sm" onClick={() => setMoving(true)}>
                      {t('gallery.move.action', { count: selectedIds.size })}
                    </Button>
                  ) : null}
                </div>
              </div>

              <MediaUploader
                entityType={GALLERY_MEDIA_ENTITY_TYPE}
                entityId={null}
                folderId={scope === 'unfiled' ? undefined : scope}
                maxFiles={UPLOAD_MAX_FILES}
                onChange={() => void media.refetch()}
              />

              {media.isPending ? <CardGridSkeleton cards={6} /> : null}

              {!media.isPending && items.length === 0 ? (
                <EmptyState
                  title={t('gallery.emptyTitle')}
                  description={t('gallery.emptyDescription')}
                />
              ) : null}

              {items.length > 0 ? (
                <GalleryMediaGrid
                  items={items}
                  selectable={selecting}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  onOpen={setActiveMediaId}
                />
              ) : null}

              {media.hasNextPage ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  disabled={media.isFetchingNextPage}
                  onClick={() => void media.fetchNextPage()}
                >
                  {t('gallery.loadMore')}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>

      <AddFolderDialog open={addingFolder} onOpenChange={setAddingFolder} folders={folders} />

      <RenameFolderDialog
        folder={renamingFolder}
        folders={folders}
        onOpenChange={(open) => !open && setRenamingFolder(null)}
      />

      <MoveMediaDialog
        open={moving}
        onOpenChange={setMoving}
        folders={folders}
        mediaIds={[...selectedIds]}
        onMoved={() => {
          resetSelection();
          void media.refetch();
        }}
      />

      <MediaLightbox
        items={[...items]}
        activeId={activeMediaId}
        onActiveIdChange={setActiveMediaId}
      />
    </>
  );
}

export default GalleryPage;
